/**
 * Field weights of the Google Merchant Center baseline (`scoring_rules/default/COMMON/
 * GOOGLE_MERCHANT_CENTER/v2.yaml` on the content service, `field_weights`). The score API sends
 * no weights, so points lost are computed in the app from this copy — keep it in step with the
 * yaml when the rules version moves. Keys are the field codes `/scores/products/{pid}` returns.
 */

/** GMC COMMON v2 `field_weights`, verbatim. Weights are relative, not percentages. */
export const GMC_COMMON_V2_FIELD_WEIGHTS = {
  TITLE: 20,
  DESCRIPTION: 22,
  STRUCTURED_TITLE: 3,
  STRUCTURED_DESCRIPTION: 3,
  SHORT_TITLE: 2,
  PRODUCT_HIGHLIGHT: 5,
  PRODUCT_DETAIL: 5,
  CATEGORY: 4,
  GOOGLE_PRODUCT_CATEGORY: 4,
  PRODUCT_TYPE: 4,
  BRAND: 4,
  GTIN: 2,
  MPN: 0.5,
  IDENTIFIER_EXISTS: 0.5,
  ITEM_GROUP_ID: 1,
  COLOR: 3,
  MATERIAL: 2,
  PATTERN: 1,
  SIZE: 2,
  SIZE_TYPE: 0.5,
  SIZE_SYSTEM: 0.5,
  GENDER: 2,
  AGE_GROUP: 1,
  PRODUCT_LENGTH: 0.5,
  PRODUCT_WIDTH: 0.5,
  PRODUCT_HEIGHT: 0.5,
  PRODUCT_WEIGHT: 0.5,
  SHIPPING_LENGTH: 0.25,
  SHIPPING_WIDTH: 0.25,
  SHIPPING_HEIGHT: 0.25,
  SHIPPING_WEIGHT: 0.25,
  IS_BUNDLE: 0.5,
  MULTIPACK: 0.5,
  CONDITION: 0.5,
  AVAILABILITY: 1,
  IMAGE_LINK: 2,
  ADDITIONAL_IMAGE_LINK: 0.5,
} as const satisfies Record<string, number>;

/** A field code the GMC COMMON v2 rules weight. */
export type WeightedField = keyof typeof GMC_COMMON_V2_FIELD_WEIGHTS;

/** Type guard: whether a score API field code is one the v2 rules weight. */
export function isWeightedField(field: string): field is WeightedField {
  return Object.prototype.hasOwnProperty.call(GMC_COMMON_V2_FIELD_WEIGHTS, field);
}

/** A field's weight; 0 for a field the rules don't weight (it then costs no points). */
export function fieldWeight(field: string): number {
  return isWeightedField(field) ? GMC_COMMON_V2_FIELD_WEIGHTS[field] : 0;
}

/** The sum of the weights of the fields actually scored — the denominator of points lost. */
export function scoredWeightTotal(scoredFields: readonly string[]): number {
  return [...new Set(scoredFields)].reduce((sum, field) => sum + fieldWeight(field), 0);
}
